import { useState } from "react";
import { ChevronDown, SlidersHorizontal } from "lucide-react";
import {
  CLASS_FILTER_ORDER,
  formatMessengerMonth,
  messengerClassHint,
  messengerClassLabel,
  messengerPersonaHint,
  messengerPersonaLabel,
  PERSONA_FILTER_ORDER,
} from "@/lib/messengerStoryLabels";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type SectionId = "class" | "persona" | "month" | "purchase";

type Props = {
  classOptions: [string, number][];
  personaOptions: [string, number][];
  monthOptions: [string, number][];
  storyClass?: string;
  persona?: string;
  month?: string;
  hasPurchase?: boolean;
  totalAll: number;
  onClassChange: (v: string | undefined) => void;
  onPersonaChange: (v: string | undefined) => void;
  onMonthChange: (v: string | undefined) => void;
  onPurchaseChange: (v: boolean | undefined) => void;
};

function FilterSection({
  id,
  title,
  description,
  open,
  onToggle,
  children,
}: {
  id: string;
  title: string;
  description: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="border-b border-slate-100 last:border-0">
      <button
        type="button"
        id={`filter-section-${id}`}
        className="flex w-full items-center justify-between py-3 text-left"
        onClick={onToggle}
        aria-expanded={open}
      >
        <div>
          <div className="text-sm font-semibold text-slate-900">{title}</div>
          <div className="text-xs text-slate-500">{description}</div>
        </div>
        <ChevronDown
          className={cn("h-4 w-4 shrink-0 text-slate-400 transition-transform", open && "rotate-180")}
        />
      </button>
      {open && <div className="pb-4">{children}</div>}
    </div>
  );
}

function SidebarContent(props: Props) {
  const {
    classOptions,
    personaOptions,
    monthOptions,
    storyClass,
    persona,
    month,
    hasPurchase,
    totalAll,
    onClassChange,
    onPersonaChange,
    onMonthChange,
    onPurchaseChange,
  } = props;

  const [openSections, setOpenSections] = useState<Record<SectionId, boolean>>({
    class: true,
    persona: false,
    month: false,
    purchase: false,
  });

  const toggle = (id: SectionId) =>
    setOpenSections((s) => ({ ...s, [id]: !s[id] }));

  return (
  <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
    <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-900">
      <SlidersHorizontal className="h-4 w-4 text-slate-500" />
      Doprecyzuj wyniki
    </div>

    <FilterSection
      id="class"
      title="Rodzaj wiadomości"
      description="Co głównie pisze ta osoba?"
      open={openSections.class}
      onToggle={() => toggle("class")}
    >
      <ul className="space-y-1">
        <li>
          <label className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-slate-50">
            <input
              type="radio"
              name="msg-class"
              checked={!storyClass}
              onChange={() => onClassChange(undefined)}
              className="border-slate-300 text-brand focus:ring-brand"
            />
            <span className="text-sm text-slate-700">Wszystkie ({totalAll})</span>
          </label>
        </li>
        {classOptions.map(([key, count]) => (
          <li key={key}>
            <label
              className="flex cursor-pointer items-start gap-2 rounded-lg px-2 py-1.5 hover:bg-slate-50"
              title={messengerClassHint(key)}
            >
              <input
                type="radio"
                name="msg-class"
                checked={storyClass === key}
                onChange={() => onClassChange(key)}
                className="mt-0.5 border-slate-300 text-brand focus:ring-brand"
              />
              <span className="text-sm text-slate-700">
                {messengerClassLabel(key)}{" "}
                <span className="text-slate-400">({count})</span>
              </span>
            </label>
          </li>
        ))}
      </ul>
    </FilterSection>

    {personaOptions.length > 0 && (
      <FilterSection
        id="persona"
        title="Kim jest osoba"
        description="Z czego widać, kto pisze"
        open={openSections.persona}
        onToggle={() => toggle("persona")}
      >
        <ul className="space-y-1">
          {personaOptions.map(([key, count]) => (
            <li key={key}>
              <label
                className="flex cursor-pointer items-start gap-2 rounded-lg px-2 py-1.5 hover:bg-slate-50"
                title={messengerPersonaHint(key)}
              >
                <input
                  type="checkbox"
                  checked={persona === key}
                  onChange={() => onPersonaChange(persona === key ? undefined : key)}
                  className="mt-0.5 rounded border-slate-300 text-brand focus:ring-brand"
                />
                <span className="text-sm text-slate-700">
                  {messengerPersonaLabel(key)}{" "}
                  <span className="text-slate-400">({count})</span>
                </span>
              </label>
            </li>
          ))}
        </ul>
      </FilterSection>
    )}

    {monthOptions.length > 0 && (
      <FilterSection
        id="month"
        title="Kiedy wysłano"
        description="Miesiąc wiadomości"
        open={openSections.month}
        onToggle={() => toggle("month")}
      >
        <select
          value={month ?? ""}
          onChange={(e) => onMonthChange(e.target.value || undefined)}
          className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
        >
          <option value="">Dowolny miesiąc</option>
          {monthOptions.map(([key, count]) => (
            <option key={key} value={key}>
              {formatMessengerMonth(key)} ({count})
            </option>
          ))}
        </select>
      </FilterSection>
    )}

    <FilterSection
      id="purchase"
      title="Zakupy"
      description="Powiązanie z Easycart"
      open={openSections.purchase}
      onToggle={() => toggle("purchase")}
    >
      <label className="flex cursor-pointer items-start gap-2 rounded-lg px-2 py-1.5 hover:bg-slate-50">
        <input
          type="checkbox"
          checked={hasPurchase === true}
          onChange={() => onPurchaseChange(hasPurchase ? undefined : true)}
          className="mt-0.5 rounded border-slate-300 text-brand focus:ring-brand"
        />
        <span className="text-sm text-slate-700">
          E-mail z DM widoczny w zamówieniach
        </span>
      </label>
    </FilterSection>
  </div>
  );
}

export default function MessengerFilterSidebar(props: Props) {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <>
      <div className="mb-4 lg:hidden">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="border-slate-200 bg-white text-slate-700"
          onClick={() => setMobileOpen(true)}
        >
          <SlidersHorizontal className="mr-2 h-4 w-4" />
          Filtry
        </Button>
      </div>

      <div className="hidden lg:block lg:w-72 lg:shrink-0">
        <div className="sticky top-6">
          <SidebarContent {...props} />
        </div>
      </div>

      {mobileOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-slate-900/40 lg:hidden"
            onClick={() => setMobileOpen(false)}
            aria-hidden
          />
          <div className="fixed inset-y-0 left-0 z-50 w-full max-w-sm overflow-y-auto bg-slate-50 p-4 lg:hidden">
            <div className="mb-4 flex items-center justify-between">
              <span className="font-semibold text-slate-900">Filtry</span>
              <Button type="button" variant="ghost" size="sm" onClick={() => setMobileOpen(false)}>
                Zamknij
              </Button>
            </div>
            <SidebarContent {...props} />
          </div>
        </>
      )}
    </>
  );
}

export function buildClassOptions(
  classMap: Record<string, number>,
): [string, number][] {
  const opts = CLASS_FILTER_ORDER
    .filter((key) => classMap[key])
    .map((key) => [key, classMap[key]] as [string, number]);
  const extra = Object.entries(classMap).filter(
    ([key]) => !CLASS_FILTER_ORDER.includes(key),
  );
  return [...opts, ...extra];
}

export function buildPersonaOptions(
  personaMap: Record<string, number>,
): [string, number][] {
  const opts = PERSONA_FILTER_ORDER
    .filter((key) => personaMap[key])
    .map((key) => [key, personaMap[key]] as [string, number]);
  const extra = Object.entries(personaMap).filter(
    ([key]) => !PERSONA_FILTER_ORDER.includes(key),
  );
  return [...opts, ...extra];
}
